import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { NormsInfo } from '../types/NormsInfo';
import { normsApi } from '../services/api';
import { validateTab } from '../utils/validation';

interface NormsContextType {
  normsInfo: NormsInfo;
  setNormsInfo: (info: NormsInfo | ((prev: NormsInfo) => NormsInfo)) => void;
  calculateNorms: () => Promise<void>;
  generatePdf: (patientId?: number | '') => Promise<void>;
  savePatientDetails: (patientId: number, userId?: number | null, data?: NormsInfo | Record<string, unknown>) => Promise<void>;
  savePatientDetailsOnContinue: (patientId: number, userId?: number | null, data?: NormsInfo | Record<string, unknown>) => Promise<void>;
  loadSampleData: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  hasCalculated: boolean;
  errorFields: string[];
  setErrorFields: (fields: string[]) => void;
  fieldErrorMessages: Record<string, string>;
  setFieldErrorMessages: (messages: Record<string, string>) => void;
  firstErrorTabId: string | null;
  setFirstErrorTabId: (tabId: string | null) => void;
}

const NormsContext = createContext<NormsContextType | undefined>(undefined);

export const useNorms = () => {
  const context = useContext(NormsContext);
  if (!context) {
    throw new Error('useNorms must be used within a NormsProvider');
  }
  return context;
};

const getDefaultTestingDate = () => new Date().toISOString().split('T')[0];

export const NormsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [normsInfo, setNormsInfo] = useState<NormsInfo>(() => ({ testingdate: getDefaultTestingDate() }));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [fieldErrorMessages, setFieldErrorMessages] = useState<Record<string, string>>({});
  const [firstErrorTabId, setFirstErrorTabId] = useState<string | null>(null);

  const prepareRequestData = (source: NormsInfo): NormsInfo => {
    const dataToSend: any = { ...source };

    const convertDate = (dateValue: any): string | null => {
      if (!dateValue) return null;
      if (typeof dateValue === 'string') {
        const trimmed = dateValue.trim();
        if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null')
          return null;
        
        // Convert MM/DD/YYYY to yyyy-mm-dd if needed
        let dateStr = trimmed;
        if (trimmed.includes('/')) {
          const parts = trimmed.split('/');
          if (parts.length === 3) {
            const [month, day, year] = parts;
            dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
        } else if (trimmed.includes('-')) {
          // Already in ISO format, just get the date part
          dateStr = trimmed.split('T')[0];
        }
        
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date.toISOString();
      }
      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) return null;
        return dateValue.toISOString();
      }
      return null;
    };

    // Convert required dates
    const dobConverted = convertDate(dataToSend.dob);
    if (!dobConverted) {
      throw new Error('Date of Birth is required and must be a valid date');
    }
    dataToSend.dob = dobConverted;

    const testingDateConverted = convertDate(dataToSend.testingdate);
    if (!testingDateConverted) {
      throw new Error('Testing Date is required and must be a valid date');
    }
    dataToSend.testingdate = testingDateConverted;

    // fuv is optional - only include if valid
    const fuvConverted = convertDate(dataToSend.fuv);
    if (fuvConverted) {
      dataToSend.fuv = fuvConverted;
    } else {
      delete dataToSend.fuv;
    }

    // Clean undefined/null/empty string values
    const keysToDelete: string[] = [];
    Object.keys(dataToSend).forEach((key) => {
      const value = dataToSend[key];
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (key === 'fuv' && (!value || value === ''))
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => {
      delete dataToSend[key];
    });

    if (
      dataToSend.fuv === '' ||
      dataToSend.fuv === null ||
      dataToSend.fuv === undefined
    ) {
      delete dataToSend.fuv;
    }

    // Persist lookup IDs in payload JSON (not display text).
    // Keep backward compatibility by accepting stringified numeric IDs.
    const examinerIdNum =
      dataToSend.examinerId !== undefined && dataToSend.examinerId !== null && dataToSend.examinerId !== ''
        ? Number(dataToSend.examinerId)
        : null;
    if (examinerIdNum !== null && Number.isFinite(examinerIdNum) && examinerIdNum > 0) {
      dataToSend.examinerId = examinerIdNum;
      delete dataToSend.examiner;
    }

    const placeTestingIdNum =
      dataToSend.placeTestingId !== undefined && dataToSend.placeTestingId !== null && dataToSend.placeTestingId !== ''
        ? Number(dataToSend.placeTestingId)
        : null;
    if (placeTestingIdNum !== null && Number.isFinite(placeTestingIdNum) && placeTestingIdNum > 0) {
      dataToSend.placeTestingId = placeTestingIdNum;
      delete dataToSend.place;
    }

    return dataToSend;
  };

  const calculateNorms = async () => {
    // Validate all tabs with mandatory fields (and range-only checks like Language/HVLT/WMS-IV) before calculating norms
    const tabsToValidate = ['general', 'screening', 'adas', 'intellectual', 'language', 'wmsr', 'listlearning', 'personality', 'adls', 'visuospatial', 'motor'];
    const tabDisplayNames: Record<string, string> = {
      general: 'General',
      screening: 'Screening/Premorbid',
      adas: 'ADAS',
      intellectual: 'Intellectual',
      language: 'Language',
      wmsr: 'WMS-IV',
      listlearning: 'List Learning',
      personality: 'Personality',
      adls: 'ADLs',
      visuospatial: 'Visuospatial',
      motor: 'Motor',
    };

    const allErrorFields: string[] = [];
    const messages: Record<string, string> = {};
    let firstErrorTabIdFound: string | null = null;
    let firstErrorMessage: string | null = null;

    for (const tabId of tabsToValidate) {
      const validation = validateTab(tabId, normsInfo);
      if (!validation.isValid) {
        validation.errorFields.forEach((field, i) => {
          allErrorFields.push(field);
          messages[field] = validation.errors[i] ?? '';
        });
        if (!firstErrorTabIdFound) {
          firstErrorTabIdFound = tabId;
          firstErrorMessage = validation.errors[0] ?? 'Required field is missing';
        }
      }
    }

    if (allErrorFields.length > 0) {
      const tabName = firstErrorTabIdFound ? tabDisplayNames[firstErrorTabIdFound] ?? firstErrorTabIdFound : '';
      const message = tabName && firstErrorMessage
        ? `${tabName}: ${firstErrorMessage}`
        : 'Fill all the required fields to calculate norms';
      setError(message);
      setErrorFields(allErrorFields);
      setFieldErrorMessages(messages);
      setFirstErrorTabId(firstErrorTabIdFound);
      return;
    }

    // Clear previous errors when validation passes
    setErrorFields([]);
    setFieldErrorMessages({});
    setFirstErrorTabId(null);

    setIsLoading(true);
    setError(null);

    try {
      const dataToSend = prepareRequestData(normsInfo);
      // Log the data being sent for debugging (remove in production)
      // console.log('Sending data to API (calculate):', {
      //   dob: dataToSend.dob,
      //   testingdate: dataToSend.testingdate,
      //   fuv: dataToSend.fuv,
      //   hasFuv: 'fuv' in dataToSend,
      //   allKeys: Object.keys(dataToSend),
      // });

      const result = await normsApi.calculateNorms(dataToSend);
      setNormsInfo(result);
      setHasCalculated(true);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || 'Failed to calculate norms';
      setError(errorMessage);
      console.error('Calculate Norms Error:', err.response?.data || err);
    } finally {
      setIsLoading(false);
    }
  };

  const savePatientDetails = async (
    patientId: number,
    userId?: number | null,
    data?: NormsInfo | Record<string, unknown>
  ) => {
    const payload = data ?? normsInfo;
    const dataToSend = prepareRequestData(payload as NormsInfo);
    const payloadWithIds = { ...dataToSend, patientId } as Record<string, unknown>;
    try {
      const storedUser = localStorage.getItem('user');
      let resolvedUserId: number | null = userId ?? null;
      if (resolvedUserId == null && storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed && typeof parsed.id === 'number') {
            resolvedUserId = parsed.id;
          }
        } catch {
          // ignore
        }
      }
      await normsApi.savePatientDetails(patientId, resolvedUserId, payloadWithIds);
    } catch (err) {
      console.error('Save patient details error:', err);
      setError((err as any)?.response?.data?.error || (err as Error)?.message || 'Failed to save patient details');
    }
  };

  const savePatientDetailsOnContinue = async (
    patientId: number,
    userId?: number | null,
    data?: NormsInfo | Record<string, unknown>
  ) => {
    const payload = data ?? normsInfo;
    const dataToSend = prepareRequestData(payload as NormsInfo);
    const payloadWithIds = { ...dataToSend, patientId } as Record<string, unknown>;
    try {
      const storedUser = localStorage.getItem('user');
      let resolvedUserId: number | null = userId ?? null;
      if (resolvedUserId == null && storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          if (parsed && typeof parsed.id === 'number') {
            resolvedUserId = parsed.id;
          }
        } catch {
          // ignore
        }
      }
      const result = await normsApi.savePatientDetailsOnContinue(patientId, resolvedUserId, payloadWithIds);
      if (result && typeof result.id === 'number') {
        try {
          localStorage.setItem('draftPatientDetailsId', String(result.id));
        } catch {
          // ignore storage errors
        }
      }
    } catch (err) {
      console.error('Save patient details on continue error:', err);
      setError((err as any)?.response?.data?.error || (err as Error)?.message || 'Failed to save patient details');
    }
  };

  const generatePdf = async (patientId?: number | '') => {
    // Snapshot history edit mode before generating PDF so we can both
    // decide whether this is an "edit" flow and reset it correctly after.
    let historyEditModeBefore: string | null = null;

    try {
      historyEditModeBefore = localStorage.getItem('historyEditMode');
    } catch {
      historyEditModeBefore = null;
    }

    // Require a selected patient before generating a PDF
    if (!patientId || typeof patientId !== 'number') {
      setError('Please select a patient before generating a PDF.');
      setErrorFields([]);
      return;
    }

    // Validate all tabs with mandatory fields (and range-only checks like Language/HVLT/WMS-IV) before generating PDF
    const tabsToValidate = ['general', 'screening', 'adas', 'intellectual', 'language', 'wmsr', 'listlearning', 'personality', 'adls', 'visuospatial', 'motor'];
    const tabDisplayNames: Record<string, string> = {
      general: 'General',
      screening: 'Screening/Premorbid',
      adas: 'ADAS',
      intellectual: 'Intellectual',
      language: 'Language',
      wmsr: 'WMS-IV',
      listlearning: 'List Learning',
      personality: 'Personality',
      adls: 'ADLs',
      visuospatial: 'Visuospatial',
      motor: 'Motor',
    };
    const allErrorFields: string[] = [];
    const messages: Record<string, string> = {};
    let firstErrorTabIdFound: string | null = null;
    let firstErrorMessage: string | null = null;

    for (const tabId of tabsToValidate) {
      const validation = validateTab(tabId, normsInfo);
      if (!validation.isValid) {
        validation.errorFields.forEach((field, i) => {
          allErrorFields.push(field);
          messages[field] = validation.errors[i] ?? '';
        });
        if (!firstErrorTabIdFound) {
          firstErrorTabIdFound = tabId;
          firstErrorMessage = validation.errors[0] ?? 'Required field is missing';
        }
      }
    }

    // If there are validation errors, show specific message and return
    if (allErrorFields.length > 0) {
      const tabName = firstErrorTabIdFound ? tabDisplayNames[firstErrorTabIdFound] ?? firstErrorTabIdFound : '';
      const message = tabName && firstErrorMessage
        ? `${tabName}: ${firstErrorMessage}`
        : 'Fill all the required fields to generate PDF';
      setError(message);
      setErrorFields(allErrorFields);
      setFieldErrorMessages(messages);
      setFirstErrorTabId(firstErrorTabIdFound);
      return;
    }

    // Clear error fields if validation passes
    setErrorFields([]);
    setFieldErrorMessages({});
    setFirstErrorTabId(null);

    setIsLoading(true);
    setError(null);
    try {
      const dataToSend = prepareRequestData(normsInfo);
      console.log('dataToSend for PDF generation:', dataToSend);

      // Attach patient and doctor identifiers
      (dataToSend as any).patientId = patientId;
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          if (parsed && typeof parsed.id === 'number') {
            (dataToSend as any).doctorId = parsed.id;
          }
          // Use logged-in user's name as neuropsychologist (do not use default "Angela Larery, Ph.D.")
          if (parsed && typeof parsed.fullName === 'string' && parsed.fullName.trim() !== '') {
            (dataToSend as any).neuropsychologist = parsed.fullName.trim();
          }
        }
      } catch {
        // Ignore localStorage parsing errors
      }

      // Attach patientDetailsId from localStorage (set during Continue/save or history flows)
      try {
        const draftIdRaw = localStorage.getItem('draftPatientDetailsId');
        if (draftIdRaw) {
          const parsedId = Number(draftIdRaw);
          if (!Number.isNaN(parsedId) && parsedId > 0) {
            (dataToSend as any).patientDetailsId = parsedId;
          }
        }
      } catch {
        // Ignore localStorage errors
      }

      // IMPORTANT: Always override any stale `edit` value that might exist in loaded history JSON.
      // Backend uses this to decide whether to force-update a specific history record.
      // Required behavior:
      // - Upload flow sets historyEditMode = '0' -> send edit = 0
      // - All other flows keep historyEditMode = '1' -> send edit = 1
      (dataToSend as any).edit = historyEditModeBefore === '0' ? 0 : 1;

      const blob = await normsApi.generatePdf(dataToSend);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Assessment-Report-${normsInfo.ptname || 'Patient'}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // After successful PDF generation, only normalize upload flow:
      // - When coming from upload (historyEditModeBefore === '0'), switch back to normal flow (set '1').
      // - When coming from edit/normal flow (historyEditModeBefore === '1'), do not change it.
      try {
        if (historyEditModeBefore === '0') {
          localStorage.setItem('historyEditMode', '1');
        }
      } catch {
        // Ignore storage errors
      }
    } catch (err: any) {
      let errorMessage = 'Failed to generate PDF';
      
      // Handle blob error responses - when responseType is 'blob', error responses are also blobs
      if (err.response && err.response.data instanceof Blob) {
        try {
          const errorText = await err.response.data.text();
          const errorJson = JSON.parse(errorText);
          // Extract error message from the parsed JSON
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (parseError) {
          // If parsing fails, check if it's a JSON string in the blob
          try {
            const errorText = await err.response.data.text();
            if (errorText.trim().startsWith('{')) {
              const errorJson = JSON.parse(errorText);
              errorMessage = errorJson.error || errorJson.message || errorMessage;
            } else {
              errorMessage = errorText || errorMessage;
            }
          } catch {
            // If all parsing fails, use the status code message
            errorMessage = err.response?.status === 400 
              ? 'Bad request. Please check your data and try again.'
              : errorMessage;
          }
        }
      } else {
        // Handle regular JSON error responses
        errorMessage = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.message || 
                      errorMessage;
      }
      
      setError(errorMessage);
      console.error('Generate PDF Error:', err.response?.data || err);
    } finally {
      setIsLoading(false);
    }
  };

  /** Strip time from date strings (e.g. "1974-12-01T12:19:56" -> "1974-12-01") for display/state */
  const toDateOnly = (v: string | null | undefined): string | undefined => {
    if (v == null || typeof v !== 'string' || !v.trim()) return undefined;
    const s = v.trim();
    return s.includes('T') ? s.split('T')[0] : s;
  };

  const loadSampleData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const sampleData = await normsApi.getSampleData();
      const normalized = { ...sampleData } as NormsInfo;
      if (sampleData.dob != null) {
        const d = toDateOnly(sampleData.dob);
        if (d !== undefined) normalized.dob = d;
      }
      if (sampleData.testingdate != null) {
        const d = toDateOnly(sampleData.testingdate);
        if (d !== undefined) normalized.testingdate = d;
      }
      if (sampleData.fuv != null) {
        const d = toDateOnly(sampleData.fuv);
        if (d !== undefined) normalized.fuv = d;
      }
      setNormsInfo(normalized);
      setHasCalculated(false); // Reset calculation state when loading new data
      setErrorFields([]);
      setFieldErrorMessages({});
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load sample data');
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
    setErrorFields([]);
    setFieldErrorMessages({});
    setFirstErrorTabId(null);
  };

  // When user edits a field and we're showing errors, re-validate so only still-invalid fields stay highlighted.
  useEffect(() => {
    if (errorFields.length === 0) return;
    const tabsToValidate = ['general', 'screening', 'adas', 'intellectual', 'language', 'wmsr', 'listlearning', 'personality', 'adls', 'visuospatial', 'motor'];
    const tabDisplayNames: Record<string, string> = {
      general: 'General',
      screening: 'Screening/Premorbid',
      adas: 'ADAS',
      intellectual: 'Intellectual',
      language: 'Language',
      wmsr: 'WMS-IV',
      listlearning: 'List Learning',
      personality: 'Personality',
      adls: 'ADLs',
      visuospatial: 'Visuospatial',
      motor: 'Motor',
    };
    const allErrorFields: string[] = [];
    const messages: Record<string, string> = {};
    let firstErrorTabIdFound: string | null = null;
    let firstErrorMessage: string | null = null;

    for (const tabId of tabsToValidate) {
      const validation = validateTab(tabId, normsInfo);
      if (!validation.isValid) {
        validation.errorFields.forEach((field, i) => {
          allErrorFields.push(field);
          messages[field] = validation.errors[i] ?? '';
        });
        if (!firstErrorTabIdFound) {
          firstErrorTabIdFound = tabId;
          firstErrorMessage = validation.errors[0] ?? 'Required field is missing';
        }
      }
    }

    setErrorFields(allErrorFields);
    setFieldErrorMessages(messages);
    if (allErrorFields.length > 0) {
      // Do not set error toast here – that would show e.g. "MOCA is required" while user
      // is still on General tab. Only Generate PDF / Calculate Norms should set the toast
      // and firstErrorTabId so the user is switched to the tab with the first error.
      // Do not set firstErrorTabId here – that would trigger App's "focus first error field" effect
      // and steal focus from the field the user is typing in. Only update which fields are invalid.
    } else {
      setError(null);
      setFirstErrorTabId(null);
    }
  }, [normsInfo]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-validate when form data changes; errorFields intentionally not in deps

  return (
    <NormsContext.Provider
      value={{
        normsInfo,
        setNormsInfo,
        calculateNorms,
        generatePdf,
        savePatientDetails,
        savePatientDetailsOnContinue,
        loadSampleData,
        isLoading,
        error,
        clearError,
        hasCalculated,
        errorFields,
        setErrorFields,
        fieldErrorMessages,
        setFieldErrorMessages,
        firstErrorTabId,
        setFirstErrorTabId,
      }}
    >
      {children}
    </NormsContext.Provider>
  );
};

